import { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Container,
  Typography,
  Box,
  Stack,
  Alert,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { NavLink as RouterLink } from 'react-router-dom';
import Slider from 'react-slick';
import ReCAPTCHA from 'react-google-recaptcha';
import useResponsive from '../hooks/useResponsive';
import Logo from '../components/logo';
import axios from '../utils/Axios';
import isExternalAuthHostname from '../utils/isExternalAuthHostname';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import FormProvider, { RHFTextField } from '../components/hook-form';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled('div')(() => ({
  width: '50%',
  padding: 50,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: localStorage.getItem("primaryColor") ?? '#46C492',
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

export default function RequestResetPage() {
  const settings = {
    dots: true,
    autoplay: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  const methods = useForm({ defaultValues: { email: '' } });
  const recaptchaRef = useRef();
  const [errorForm, setErrorForm] = useState(null);
  const [successMessage, setSuccessMessage] = useState(false);

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const mdUp = useResponsive('up', 'sm');

  const onSubmit = async (data) => {
    try {
      let captcha = null;

      if (recaptchaRef.current) {
        captcha = await recaptchaRef.current.executeAsync();
      }

      const response = await axios.post('/auth/reset-request', {
        ...data,
        captcha,
      });
      if (response.status === 200) {
        setSuccessMessage(true);
      }
    } catch (error) {
      reset();
      setErrorForm({ message: error.messages.join('\n') });
    }
  };

  return (
    <>
      <Helmet>
        <title> Request For Password Reset | {localStorage.getItem("companyName") ?? "FlowBookings"} </title>
      </Helmet>
      <StyledRoot>
        <Logo
          width="160px"
          sx={{
            position: 'fixed',
            top: { xs: 16, sm: 24, md: 40 },
            left: { xs: 16, sm: 24, md: 40 },
          }}
        />
        <Container>
          <StyledContent>
            <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
              Forgot Password?
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}
            >
              Don’t worry, we’ll send you reset instructions.
            </Typography>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              {!!errorForm && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorForm.message}
                </Alert>
              )}
              {isExternalAuthHostname() && <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey="6LdJD_olAAAAAIuRxejs1qI2PX4QUftTya_o6MIH"
              />}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Password Reset Link was Sent To Your Email. Didn't receive the
                  verification email ?
                  <Button size="small" type="submit" sx={{ ml: 2 }}>
                    Click here to Resend
                  </Button>
                </Alert>
              )}
              {!successMessage && (
                <>
                  <RHFTextField
                    name="email"
                    size="large"
                    placeholder="Email"
                    InputProps={{
                      sx: { mb: 3 },
                    }}
                  />
                  <LoadingButton
                    sx={{ mt: 2, backgroundColor: '#36B37E' }}
                    type="submit"
                    fullWidth
                    size="large"
                    variant="contained"
                    loading={isSubmitting}
                  >
                    Reset Password
                  </LoadingButton>
                </>
              )}
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ justifyContent: 'center', mt: 2 }}
              >
                <Typography
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  sx={{ color: localStorage.getItem("primaryColor") ?? '#46C492', fontWeight: 'bold' }}
                >
                  Back To Log in
                </Typography>
              </Stack>
            </FormProvider>
          </StyledContent>
        </Container>

        {mdUp && (
          <StyledSection>
            <Typography variant="h3" sx={{ mb: 3, color: '#fff' }}>
              The Ultimate Booking Solution <br /> for Webflow!
            </Typography>

            <Slider {...settings}>
              <Box
                component="img"
                src="/assets/images/products/Cloneable_7.png"
                sx={{
                  objectFit: 'cover',
                  borderRadius: 1.5,
                }}
              />
              <Box
                component="img"
                src="/assets/images/products/Cloneable_10.png"
                sx={{
                  objectFit: 'cover',
                  borderRadius: 1.5,
                }}
              />
              <Box
                component="img"
                src="/assets/images/products/Cloneable_12.png"
                sx={{
                  objectFit: 'cover',
                  borderRadius: 1.5,
                }}
              />
              <Box
                component="img"
                src="/assets/images/products/Cloneable_14.png"
                sx={{
                  objectFit: 'cover',
                  borderRadius: 1.5,
                }}
              />
            </Slider>
          </StyledSection>
        )}
      </StyledRoot>
    </>
  );
}
